import type { ClientPositionView } from "@solomon-apps/shared";
import {
    useAdvertisedApy,
    useStakingStats,
    YaasPositionStatus
} from "@solomon-apps/shared";
import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ToggleGroup,
    ToggleGroupItem
} from "@solomon-apps/shared/src/components/ui/toggle-group";
import type { BigDecimal } from "@solomon-labs/bigdecimal";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import {
    Address,
    Button as DesignButton
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { DesktopOnly, ViewOnlyHint } from "@/components/ViewOnly";
import {
    comparePositionAmounts,
    formatObservedUsdv
} from "@/hooks/positionAmounts";
import { useClientDashboard } from "@/hooks/useClientDashboard";
import { useClientPositions } from "@/hooks/useClientPositions";
import { usePortfolioTotals } from "@/hooks/usePortfolioTotals";
import { useViewOnly } from "@/hooks/useViewOnly";
import { useSyncSimMode } from "@/services/sim/useSyncSimMode";

import { PositionWalkthrough } from "./AddPosition/PositionWalkthrough";
import { PortfolioKeyStats } from "./Dashboard/components/PortfolioKeyStats";
import {
    resolveCurrentApy,
    resolveHistoricalApy,
    resolvePendingRewards
} from "./Dashboard/portfolioKeyStatsModel";
import { PositionsEmptyState } from "./Positions/components/PositionsEmptyState";
import {
    SortableColHead,
    type SortDirection
} from "./Positions/components/SortableColHead";
import {
    type Holding,
    holdingApyDisplay,
    holdingRealizedApy
} from "./Positions/holdings";
import { useHoldings } from "./Positions/useHoldings";

/** Route that puts the Positions page into add-position mode. */
const ADD_POSITION_PATH = "/positions/add";

type FilterTab = "all" | "active" | "inactive";

const TABS: { value: FilterTab; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
];

/**
 * YaaS position status → IndicationUnit `status` state. Mirrors the
 * `PROGRAM_STATE` / `ELIGIBILITY_STATE` maps in the programs console and the
 * `STATUS_ORDER` map in PositionsSummary.
 */
const STATUS_STATE: Record<
    YaasPositionStatus,
    "success" | "pending" | "error" | "disabled"
> = {
    [YaasPositionStatus.Active]: "success",
    [YaasPositionStatus.Pending]: "pending",
    [YaasPositionStatus.Rejected]: "error",
    // Suspended reads as inactive, not in-progress — grey (disabled), not amber.
    [YaasPositionStatus.Suspended]: "disabled",
    [YaasPositionStatus.Disabled]: "disabled",
    [YaasPositionStatus.Hidden]: "disabled"
};

// Status-label text color — matches the indicator dot's tone (same state →
// same color) so the dot + label read as one unit.
const STATUS_TEXT_COLOR: Record<
    "success" | "pending" | "error" | "disabled",
    string
> = {
    success: "text-success-base",
    pending: "text-warning-base",
    error: "text-error-base",
    disabled: "text-disabled-base"
};

/**
 * Shared column template for the positions table — used by both the header
 * row and each `ListItem` row so the columns line up:
 * status · surface · position · address · destination · balance ·
 * APY · rewards earned · rewards pending.
 *
 * Surface + Position are sized comparably (`minmax(120px,1.2fr)`) so the two
 * text fields stay well-proportioned to the numeric columns rather than
 * dominating the row width.
 *
 * Responsive via CONTAINER queries (`@container/postable` on the table
 * wrapper, so columns drop by the table's own width — not the viewport — and
 * it stays correct in any layout). As the table narrows, things drop in this
 * priority order: the status LABEL collapses to its dot + rewards-pending drops
 * (both at @6xl), then rewards-earned, destination, address, and finally
 * surface. Each breakpoint lists only the tracks for what's visible at that
 * width; the matching header
 * + row cells carry the same `hidden @Nxl/postable:*` toggle, and a
 * `display:none` cell drops out of the grid so the remaining cells fill the
 * listed tracks in order — keeping header and rows aligned for free. The
 * status column's first track is a 16px dot until @6xl, where it widens to
 * fit the label.
 */
// Grid-template only (no `grid` display) so each consumer can gate it with
// `hidden md:grid` — the table is desktop-only; mobile renders cards instead.
const ROW_GRID =
    // base (<@2xl): status-dot · position · balance · APY
    "grid-cols-[16px_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(72px,0.8fr)] " +
    // +surface
    "@2xl/postable:grid-cols-[16px_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(72px,0.8fr)] " +
    // +address
    "@3xl/postable:grid-cols-[16px_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(104px,1fr)_minmax(72px,0.8fr)] " +
    // +destination
    "@4xl/postable:grid-cols-[16px_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(116px,1.1fr)_minmax(104px,1fr)_minmax(72px,0.8fr)] " +
    // +rewards-earned
    "@5xl/postable:grid-cols-[16px_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(116px,1.1fr)_minmax(104px,1fr)_minmax(72px,0.8fr)_minmax(120px,1.1fr)] " +
    // +rewards-pending AND status label (first track widens to fit the dot + text)
    "@6xl/postable:grid-cols-[minmax(96px,0.8fr)_minmax(120px,1.2fr)_minmax(120px,1.2fr)_minmax(104px,1fr)_minmax(116px,1.1fr)_minmax(104px,1fr)_minmax(72px,0.8fr)_minmax(120px,1.1fr)_minmax(120px,1.1fr)] " +
    "items-center gap-4";

/** Table-cell amount: grouped, two decimals, no currency sign ("8,421.36"). */
function fmt(value: BigDecimal | null): string {
    if (value === null) return "—";
    const [whole, fraction] = value.round(2).toFixed(2, true).split(".");
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction}`;
}

/**
 * The address to surface for a position. Squads multisig positions hold their
 * funds in the vault PDA, not the multisig account, so we show the vault — the
 * backend returns it as `usdvBalanceAddress` (the same vault-aware field the
 * balance/rewards code paths use). EOAs have no vault and fall back to the
 * account address. Used by both the Address cell and its sort so the two never
 * disagree.
 */
export function positionAddress(p: ClientPositionView): string {
    return p.usdvBalanceAddress ?? p.account.address;
}

/**
 * Which of the three mutually exclusive regions the page renders below its
 * header:
 *
 * - `first-run` — the zero-positions onboarding screen, which hosts the same
 *   walkthrough under a "Start Earning" pitch.
 * - `add-position` — the `/positions/add` mode: the walkthrough on its own,
 *   sitting under the key-stats.
 * - `table` — the normal positions table.
 */
export type PositionsView = "first-run" | "add-position" | "table";

interface PositionsViewInput {
    /** Current route — `/positions/add` requests the walkthrough. */
    pathname: string;
    isLoading: boolean;
    hasError: boolean;
    hasPositions: boolean;
    /** Mobile — positions can be read but not added. */
    viewOnly: boolean;
}

/**
 * Resolve the page's region. `first-run` deliberately outranks an explicit
 * `/positions/add`: with no positions there is no table to return to, so the
 * warmer first-run framing is the better screen to land on.
 *
 * A failed fetch never counts as "no positions" — it falls through to `table`,
 * which renders the load error rather than claiming an empty portfolio. Mobile
 * is view-only, so it keeps the normal layout instead of being pushed into a
 * flow it can't complete. OnboardingGate owns access to both routes; Program
 * Assignments do not determine whether the client can enter the walkthrough.
 */
export function resolvePositionsView({
    pathname,
    isLoading,
    hasError,
    hasPositions,
    viewOnly
}: PositionsViewInput): PositionsView {
    if (!isLoading && !hasPositions && !hasError && !viewOnly) {
        return "first-run";
    }
    return pathname === ADD_POSITION_PATH ? "add-position" : "table";
}

/**
 * YaaS Client Console — Positions page.
 *
 * A full-width sortable table of the client's positions, mirroring the
 * programs console's Agents table: a page header (h1 + filter tabs), a
 * count + "+New Address" row, then a sortable column-header row over the
 * shared `ROW_GRID`, then `<List selectable="single">` rows each with a
 * leading status dot and right-aligned numeric cells. Clicking a row opens
 * that position's existing detail/edit view at `/positions/:uuid`.
 */
export function PositionsPage(): React.ReactElement {
    // Enable sim mode when the page is visited with `?sim=1`. Belt-and-
    // suspenders: `isSimMode()` already reads the URL, but this persists the
    // flag to sessionStorage so it survives onward navigation.
    useSyncSimMode();

    const { positions, isLoading, error } = useClientPositions();
    const { totalUsdvPositions, totalUsdvEarned, isLoadingPositions } =
        usePortfolioTotals(positions);
    const { data: stakingStats, isLoading: statsLoading } = useStakingStats();
    const { apy, isLoading: apyLoading } = useAdvertisedApy(
        stakingStats,
        statsLoading
    );
    const {
        dashboard,
        isLoading: dashboardLoading,
        error: dashboardError
    } = useClientDashboard();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const viewOnly = useViewOnly();

    const [showReviewReceipt, setShowReviewReceipt] = useState(false);
    useEffect(() => {
        setShowReviewReceipt(false);
    }, [pathname]);
    const [tab, setTab] = useState<FilterTab>("all");

    // One row per holding: a wallet under several Programs is one row, and
    // Programs never surface (see `Positions/holdings.ts`).
    const holdings = useHoldings(positions);
    const visible = holdings.filter((h) =>
        tab === "all"
            ? true
            : tab === "active"
              ? h.status === YaasPositionStatus.Active
              : h.status !== YaasPositionStatus.Active
    );

    const hasPositions = positions.length > 0;

    // "+New Address" navigates to /positions/add, which renders the
    // walkthrough inline (in place of the controls row + table) so it still
    // eases in under the existing header + key-stats. The mode is derived from
    // the route rather than local state so the flow has a real URL: browser
    // Back leaves it (the only way out by design — an in-page escape control
    // broke the header rhythm), and a refresh keeps you in it instead of
    // silently dropping you on the table. On success the
    // walkthrough's onSubmitted navigates back and the new position appears in
    // the table (React Query has already invalidated).
    const resolvedView = resolvePositionsView({
        pathname,
        isLoading,
        hasError: Boolean(error),
        hasPositions,
        viewOnly
    });
    // A successful create refreshes positions before the walkthrough renders
    // its receipt. Keep the first-run flow mounted until the client leaves it.
    const [firstRunPath, setFirstRunPath] = useState<string | null>(null);
    useEffect(() => {
        if (resolvedView === "first-run") setFirstRunPath(pathname);
        else if (firstRunPath !== pathname) setFirstRunPath(null);
    }, [resolvedView, pathname, firstRunPath]);
    const view = firstRunPath === pathname ? "first-run" : resolvedView;
    const finishWalkthrough = (): void => {
        setFirstRunPath(null);
        setShowReviewReceipt(false);
        navigate("/positions");
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col pt-3 md:pt-6 w-full"
        >
            {/* Page header */}
            <section className="pb-3 md:pb-6">
                <div className="px-4 md:px-6 flex flex-col gap-4">
                    <Text variant="h1" className="text-foreground-base">
                        Positions
                    </Text>
                </div>
            </section>

            {view === "first-run" ? (
                <PositionsEmptyState
                    showIntro={!hasPositions && !showReviewReceipt}
                    showReviewReceipt={showReviewReceipt}
                    onPendingReviewChange={setShowReviewReceipt}
                    viewOnly={viewOnly}
                    onSubmitted={finishWalkthrough}
                />
            ) : (
                <>
                    {/* Portfolio key-stats — wired exactly like the Dashboard. */}
                    {!showReviewReceipt && (
                        <PortfolioKeyStats
                            totalUsdvPositions={
                                isLoading ? null : totalUsdvPositions
                            }
                            isLoadingPositions={isLoading || isLoadingPositions}
                            currentApy={resolveCurrentApy({
                                apy,
                                isLoading: apyLoading,
                                positions
                            })}
                            totalUsdvEarned={
                                isLoading || error ? null : totalUsdvEarned
                            }
                            totalUsdvPending={resolvePendingRewards(positions)}
                            historicalApy={resolveHistoricalApy({
                                dashboard,
                                isLoading: dashboardLoading,
                                error: dashboardError,
                                positions
                            })}
                            totalPositions={holdings.length}
                        />
                    )}

                    {view === "add-position" ? (
                        /* Add-position flow (route: /positions/add) — eases in
                           under the header + key-stats, replacing the controls
                           + table rather than jumping to a separate page. */
                        /* The review receipt draws its own full-bleed top
                           rule; inside this padding it was inset and pushed
                           24px below the header, unlike the key-stats rule
                           that sits on the header's edge. */
                        <section
                            className={
                                showReviewReceipt
                                    ? undefined
                                    : "px-4 md:px-6 py-6 flex flex-col gap-6"
                            }
                        >
                            {/* The way out of the flow sits above the title,
                                the console's back link (link + underline,
                                short left arrow). The wizard's own "Back"
                                stays step navigation only. */}
                            {!showReviewReceipt && (
                                <div className="flex flex-col gap-2">
                                    <DesignButton
                                        variant="link"
                                        interaction="underline"
                                        size="sm"
                                        leading={<ArrowShortLeftIcon />}
                                        render={<Link to="/positions" />}
                                        className="self-start"
                                    >
                                        Positions
                                    </DesignButton>
                                    <Text
                                        variant="h2"
                                        className="text-foreground-base"
                                    >
                                        Add an Address
                                    </Text>
                                </div>
                            )}
                            {viewOnly ? (
                                /* Reachable by deep link on mobile — the
                                   "+New Address" control is dropped there. */
                                <DesktopOnly
                                    title="Add an address on desktop"
                                    description="Registering a position involves connecting and signing with your wallet, so it's desktop-only for now. Open the console on a computer to continue."
                                />
                            ) : (
                                <PositionWalkthrough
                                    onPendingReviewChange={setShowReviewReceipt}
                                    onSubmitted={finishWalkthrough}
                                    viewOnly={viewOnly}
                                />
                            )}
                        </section>
                    ) : (
                        <>
                            {/* Controls row — filter ToggleGroup + visible count on the
                        left, +New Address on the right. */}
                            <div className="px-4 md:px-6 py-3 md:py-5 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <ToggleGroup
                                        variant="outline"
                                        size="sm"
                                        spacing={2}
                                        value={[tab]}
                                        onValueChange={(value) => {
                                            const next = value[0] as
                                                | FilterTab
                                                | undefined;
                                            if (next) setTab(next);
                                        }}
                                    >
                                        {TABS.map((t) => (
                                            <ToggleGroupItem
                                                key={t.value}
                                                value={t.value}
                                                // px-3 matches the +New Address
                                                // button's horizontal padding (Button
                                                // size="xs"); the `sm` toggle size
                                                // already matches its `small` text +
                                                // h-7 height so they read as one size.
                                                className="px-3"
                                            >
                                                {t.label}
                                            </ToggleGroupItem>
                                        ))}
                                    </ToggleGroup>
                                </div>
                                {/* +New Address is a mutation entry point, so it's
                                    desktop-only — on mobile (view-only) it's
                                    dropped and the "switch to desktop" hint moves
                                    to the bottom of the table instead. */}
                                {!viewOnly && (
                                    <Button
                                        variant="raised"
                                        interaction="lifted"
                                        size="xs"
                                        onClick={() =>
                                            navigate(ADD_POSITION_PATH)
                                        }
                                    >
                                        +New Address
                                    </Button>
                                )}
                            </div>

                            {isLoading && !hasPositions ? (
                                <div className="px-4 md:px-6 py-6">
                                    <Skeleton className="h-4 w-32 rounded-none" />
                                    <Skeleton className="h-3 w-64 rounded-none mt-3" />
                                </div>
                            ) : error && !hasPositions ? (
                                /* A failed fetch must read as a failure — an empty
                                 * table here would claim "no positions" about a
                                 * portfolio we couldn't load. */
                                <div className="px-4 md:px-6 py-8">
                                    <Text
                                        variant="small"
                                        className="text-foreground-muted"
                                    >
                                        Couldn&rsquo;t load positions. Refresh
                                        the page to try again.
                                    </Text>
                                </div>
                            ) : !hasPositions ? (
                                /* Mobile empty state — desktop jumps to the
                                 * add-position walkthrough (forceAddPosition), but
                                 * mobile is view-only, so keep the layout and just
                                 * say there are none. */
                                <div className="px-4 md:px-6 py-3 border-t border-line-base">
                                    <Text
                                        variant="body"
                                        className="text-foreground-muted"
                                    >
                                        No active positions
                                    </Text>
                                </div>
                            ) : (
                                <PositionsTable
                                    holdings={visible}
                                    onSelect={(uuid) =>
                                        navigate(`/positions/${uuid}`)
                                    }
                                />
                            )}
                            {/* On mobile (view-only) the "switch to desktop" hint
                                sits at the bottom — shown whether or not there are
                                positions, since the +New Address control is
                                dropped from the header. */}
                            {viewOnly && (
                                <div className="px-4 md:px-6 py-4">
                                    <ViewOnlyHint />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </motion.div>
    );
}

/* ── Table view ──────────────────────────────────────────────── */

type SortColumn =
    | "position"
    | "surface"
    | "address"
    | "destination"
    | "balance"
    | "rewards"
    | "apy"
    | "pending";

function PositionsTable({
    holdings,
    onSelect
}: {
    holdings: Holding[];
    /** Called with the holding's primary position uuid (the detail route). */
    onSelect: (uuid: string) => void;
}): React.ReactElement {
    const [sortColumn, setSortColumn] = useState<SortColumn>("position");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const handleSort = (column: SortColumn): void => {
        if (column === sortColumn) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const sortedPositions = useMemo(() => {
        const copy = [...holdings];
        // Mirror the cells' displayed values so the sort never contradicts
        // what the user sees.
        const cmp = (a: Holding, b: Holding): number => {
            switch (sortColumn) {
                case "position":
                    return a.name.localeCompare(b.name);
                case "surface":
                    return a.surface.localeCompare(b.surface);
                case "address":
                    return a.address.localeCompare(b.address);
                case "destination":
                    return (a.rewardsDestinationAddress ?? "").localeCompare(
                        b.rewardsDestinationAddress ?? ""
                    );
                case "balance":
                    return comparePositionAmounts(a.balance, b.balance);
                case "rewards":
                    return a.rewardsEarned.cmp(b.rewardsEarned);
                case "apy":
                    return (
                        (holdingRealizedApy(a) ?? -Infinity) -
                            (holdingRealizedApy(b) ?? -Infinity) || 0
                    );
                case "pending":
                    return comparePositionAmounts(
                        a.rewardsPending,
                        b.rewardsPending
                    );
            }
        };
        copy.sort((a, b) => (sortDirection === "asc" ? cmp(a, b) : -cmp(a, b)));
        return copy;
    }, [holdings, sortColumn, sortDirection]);

    return (
        <div>
            {sortedPositions.length > 0 ? (
                <div className="@container/postable border-t border-line-base">
                    {/* Column header row — desktop only; mobile uses cards. */}
                    <div
                        className={`hidden md:grid ${ROW_GRID} px-4 md:px-6 py-2 border-b border-line-base`}
                    >
                        <span className="flex items-center min-w-0">
                            <Text
                                variant="label"
                                render={<span />}
                                className="hidden @6xl/postable:block text-foreground-muted truncate"
                            >
                                Status
                            </Text>
                        </span>
                        <SortableColHead
                            label="Surface"
                            columnKey="surface"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            className="hidden @2xl/postable:flex"
                        />
                        <SortableColHead
                            label="Position"
                            columnKey="position"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                        />
                        <SortableColHead
                            label="Address"
                            columnKey="address"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            className="hidden @3xl/postable:flex"
                        />
                        <SortableColHead
                            label="Destination Address"
                            columnKey="destination"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            className="hidden @4xl/postable:flex"
                        />
                        <SortableColHead
                            label="Balance"
                            columnKey="balance"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            align="right"
                        />
                        <SortableColHead
                            label="APY"
                            columnKey="apy"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            align="right"
                        />
                        <SortableColHead
                            label="Rewards Earned"
                            columnKey="rewards"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            align="right"
                            className="hidden @5xl/postable:flex"
                        />
                        <SortableColHead
                            label="Rewards Pending"
                            columnKey="pending"
                            activeColumn={sortColumn}
                            direction={sortDirection}
                            onSort={handleSort}
                            align="right"
                            className="hidden @6xl/postable:flex"
                        />
                    </div>

                    <List selectable="single" selected="" onSelect={onSelect}>
                        {sortedPositions.map((h) => (
                            <PositionRow key={h.key} holding={h} />
                        ))}
                    </List>
                </div>
            ) : (
                <div className="px-4 md:px-6 py-8">
                    <Text variant="small" className="text-foreground-muted">
                        No positions in this view.
                    </Text>
                </div>
            )}
        </div>
    );
}

/**
 * One positions-table row — one holding. Balance is the holding's USDv counted
 * once (live on-chain for Address holdings, observed principal for LPs); APY
 * is the holding's realized rate from completed settlements; rewards are
 * summed across the holding's underlying positions.
 */
function PositionRow({ holding: h }: { holding: Holding }): React.ReactElement {
    const displayAmount = h.balanceLoading
        ? "..."
        : formatObservedUsdv(h.balance);
    const apy = holdingApyDisplay(h);
    return (
        <ListItem
            itemKey={h.primaryUuid}
            padding="px-4 md:px-6 py-3"
            gap="gap-0"
        >
            {/* Desktop — the full table grid row. */}
            <div className={`hidden md:grid ${ROW_GRID} w-full`}>
                <span className="flex items-center gap-1.5 min-w-0">
                    <span className="shrink-0">
                        <IndicationUnit
                            variant="status"
                            state={STATUS_STATE[h.status]}
                            size={7}
                        />
                    </span>
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className={`hidden @6xl/postable:block truncate ${
                            STATUS_TEXT_COLOR[STATUS_STATE[h.status]]
                        }`}
                    >
                        {h.status}
                    </Text>
                </span>
                <Text
                    variant="fine-print"
                    render={<span />}
                    className="hidden @2xl/postable:block text-foreground-muted truncate"
                >
                    {h.surface}
                </Text>
                <Text
                    variant="fine-print"
                    render={<span />}
                    className="text-foreground-base truncate"
                >
                    {h.name}
                </Text>
                <Address
                    value={h.address}
                    textVariant="fine-print"
                    mono={false}
                    className="hidden @3xl/postable:block text-foreground-muted font-numeric"
                />
                {h.rewardsDestinationAddress ? (
                    <Address
                        value={h.rewardsDestinationAddress}
                        textVariant="fine-print"
                        mono={false}
                        className="hidden @4xl/postable:block text-foreground-muted font-numeric"
                    />
                ) : (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="hidden @4xl/postable:block text-foreground-muted font-numeric truncate"
                    >
                        Multiple destinations
                    </Text>
                )}
                <NumCell>{displayAmount}</NumCell>
                <NumCell>{apy}</NumCell>
                <NumCell className="hidden @5xl/postable:block">
                    {fmt(h.rewardsEarned)}
                </NumCell>
                <NumCell className="hidden @6xl/postable:block">
                    {fmt(h.rewardsPending)}
                </NumCell>
            </div>

            {/* Mobile — a two-row card (a table doesn't fit a phone). The status
                dot leads the whole block (outside the text) so both text rows
                align on the left: row 1 is surface/position + APY, row 2 is
                balance + earned. */}
            <div className="md:hidden flex w-full items-start gap-2">
                <span className="mt-0.5 shrink-0">
                    <IndicationUnit
                        variant="status"
                        state={STATUS_STATE[h.status]}
                        size={7}
                    />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex items-center justify-between gap-3">
                        <Text
                            variant="small"
                            render={<span />}
                            className="truncate text-foreground-base"
                        >
                            {h.surface}: {h.name}
                        </Text>
                        <Text
                            variant="small"
                            render={<span />}
                            className="shrink-0 text-foreground-base"
                        >
                            {apy} APY
                        </Text>
                    </div>
                    <div className="flex items-baseline justify-between gap-4">
                        <div className="flex items-baseline gap-1.5">
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Balance:
                            </Text>
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                {displayAmount}
                            </Text>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Earned:
                            </Text>
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                ${fmt(h.rewardsEarned)}
                            </Text>
                        </div>
                    </div>
                </div>
            </div>
        </ListItem>
    );
}

/** Right-aligned numeric table cell — fine-print, tabular. */
function NumCell({
    children,
    className
}: {
    children: React.ReactNode;
    className?: string;
}): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className={`text-foreground-base font-numeric tabular-nums text-right ${
                className ?? ""
            }`}
        >
            {children}
        </Text>
    );
}
